import { useState, useEffect } from 'react'
import { getColaSap, leerOfPdf, confirmarTareaSap } from '../../api/client'
import { repartirPdfs } from './otLogica'

// OF PDFs dropped on the work orders list: each one is read against every
// OT waiting for its OF (the SAP queue's "crear y liberar OF" tasks the user
// may run), and one that matches exactly one OT -- that no other dropped PDF
// also matches -- is confirmed right away: the OT is released with its folio
// and PDF (the server checks everything again). The rest are reported for
// the user to settle by hand (an OF held by a cancelled OT always is).

export const esPdf = (f) => !!f && (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))
export const conArchivos = (e) => Array.from(e.dataTransfer?.types || []).includes('Files')

// A file being dragged over the window (a counter: dragenter/leave fire per element).
export function useArrastreArchivos(activo = true) {
  const [arrastrando, setArrastrando] = useState(false)
  useEffect(() => {
    if (!activo) return
    let n = 0
    const entra = (e) => { if (!conArchivos(e)) return; n++; setArrastrando(true) }
    const sale = (e) => { if (!conArchivos(e)) return; n = Math.max(0, n - 1); if (!n) setArrastrando(false) }
    const sobre = (e) => { if (conArchivos(e)) e.preventDefault() }
    const fin = (e) => { if (!conArchivos(e)) return; e.preventDefault(); n = 0; setArrastrando(false) }
    window.addEventListener('dragenter', entra)
    window.addEventListener('dragleave', sale)
    window.addEventListener('dragover', sobre)
    window.addEventListener('drop', fin)
    return () => {
      window.removeEventListener('dragenter', entra)
      window.removeEventListener('dragleave', sale)
      window.removeEventListener('dragover', sobre)
      window.removeEventListener('drop', fin)
    }
  }, [activo])
  return arrastrando
}

// -> { ignorados, resultados: [{ archivo, estado, ot, folio, candidatos, mensaje }] } with estado
//    liberada | ambiguo | de_cancelada | sin_coincidencia | error | sin_destinos.
export async function procesarPdfs(lista) {
  const pdfs = Array.from(lista || []).filter(esPdf)
  const ignorados = Array.from(lista || []).length - pdfs.length
  const destinos = (await getColaSap('abiertas'))
    .filter(i => i.tarea.transaccion === 'crear_liberar_of' && i.tarea.estado === 'pendiente' && i.puede_ejecutar)
  if (!destinos.length) {
    return { ignorados, resultados: pdfs.map(f => ({ archivo: f.name, estado: 'sin_destinos' })) }
  }
  const codigo = Object.fromEntries(destinos.map(i => [i.tarea.id, i.ot_codigo]))
  const archivos = await Promise.all(pdfs.map(async file => {
    const revs = await Promise.all(destinos.map(i => leerOfPdf(i.tarea.id, file)
      .then(r => ({ id: i.tarea.id, coincide: !!r?.coincide, deCancelada: r?.folio_de_cancelada || null }))
      .catch(() => null)))
    // An OF that still sits on a cancelled OT is never confirmed from a drop: moving it
    // takes the OF off that OT for good, so the user does it by hand in the SAP queue.
    const deCancelada = revs.map(r => r?.deCancelada).find(Boolean) || null
    const coincide = deCancelada ? [] : revs.filter(r => r?.coincide).map(r => r.id)
    return { file, coincide, deCancelada }
  }))
  const asignados = repartirPdfs(archivos, {})              // { tareaId: file }
  const resultados = []
  for (const a of archivos) {
    const tareaId = Object.keys(asignados).find(id => asignados[id] === a.file)
    if (tareaId) {
      try {
        const ot = await confirmarTareaSap(Number(tareaId), { archivo: a.file })
        resultados.push({ archivo: a.file.name, estado: 'liberada', ot: ot.codigo, otId: ot.id, folio: ot.folio_of })
      } catch (err) {
        const d = err.response?.data?.detail
        resultados.push({ archivo: a.file.name, estado: 'error', ot: codigo[tareaId],
          mensaje: d?.message || (typeof d === 'string' ? d : null) })
      }
    } else if (a.deCancelada) {
      resultados.push({ archivo: a.file.name, estado: 'de_cancelada', ot: a.deCancelada })
    } else if (a.coincide.length) {
      resultados.push({ archivo: a.file.name, estado: 'ambiguo', candidatos: a.coincide.map(id => codigo[id]) })
    } else {
      resultados.push({ archivo: a.file.name, estado: 'sin_coincidencia' })
    }
  }
  return { ignorados, resultados }
}
